import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'
import { ContentStateProperty, DropdownOption, DropdownValue, LocalizedDropdownOptions } from '../../../../@types/types'
import { InputLabel } from '../../display/labelText/LabelText'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { useIsRequiredProperty } from '../../../../services/validation/requiredFieldsUtils/useIsRequiredProperty'
import { Button, Checkbox, Dropdown, TextInput, useDropdown } from 'is24-corecss'
import {
  buildDropdownOptions,
  formHasErrors,
  getNewStyles,
  handleEmpty,
  isEqualTo,
  isNoInformation,
  isValueInvalid,
  optionToValue,
} from '../dropdowns/commonUtils'
import styles from './MultiSelectDropdown.module.css'
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll'

export type MultiSelectDropdownProps = {
  id: string
  name: ContentStateProperty
  label?: string
  labelClassName?: string
  ariaLabel?: string
  disabled?: boolean
  values?: DropdownValue[]
  noInformation?: string
  style?: React.CSSProperties
  options: DropdownOption[] | LocalizedDropdownOptions
  placeholder?: string
  allowEmpty?: boolean
  errors?: string[]
  onChange: (value: any) => void
  onBlur?: () => void
  onBlurTracking?: (value: any, isUnchanged: boolean) => void
  onFocus?: () => void
}

const notNull = (o: DropdownOption) => o.value != null

const removeNoInformation = (o: DropdownOption) => o.value !== 'NO_INFORMATION'

const SaveSelectionButton = ({ label }: { label: string }) => {
  const { close } = useDropdown()

  return (
    <Button className='one-whole' onClick={close} role='menuitem'>
      {label}
    </Button>
  )
}

const valueToOption = (dropdownOptions: DropdownOption[], values: DropdownValue[] | undefined): DropdownOption[] =>
  !values
    ? dropdownOptions.filter((option) => option.value === handleEmpty(values))
    : dropdownOptions.filter((option) => values.includes(option.value))

const MultiSelectDropdown = ({
  id,
  label,
  labelClassName,
  name,
  ariaLabel,
  disabled,
  values,
  noInformation,
  style,
  options,
  placeholder,
  allowEmpty,
  errors = [],
  onChange,
  onBlur,
  onBlurTracking,
  onFocus,
}: MultiSelectDropdownProps) => {
  const { t } = useTranslation()
  const { setTouched, formState } = useFormState()
  const isFieldMandatory = useIsRequiredProperty(name)
  const triggerInputRef = useRef<HTMLInputElement>(null)

  const dropdownOptions: DropdownOption[] = useMemo(
    () => buildDropdownOptions(isFieldMandatory, options, t, noInformation),
    [isFieldMandatory, noInformation, options, t]
  )
  const [highlightValidation, setHighlightValidation] = useState(false)
  const [isUnchanged, setIsUnchanged] = useState(true)
  const selectedOptions = useMemo(() => valueToOption(dropdownOptions, values), [dropdownOptions, values])
  const selectedOptionsRef = useRef(selectedOptions)
  const [previousOptions, setPreviousOptions] = useState(selectedOptions)
  const [currentOptions, setCurrentOptions] = useState(selectedOptions)

  const [open, setOpen] = React.useState(false)
  const handleClose = () => {
    onLostFocus()
    setOpen(false)
  }
  useDisableBodyScroll(open, { mobileOnly: true })

  const hasErrors = formHasErrors(errors, formState, name)
  const isInvalid = isValueInvalid(
    currentOptions.filter(removeNoInformation).map(optionToValue),
    highlightValidation,
    allowEmpty,
    hasErrors
  )
  const inputStyles = getNewStyles(style, highlightValidation, values, isInvalid)

  const resetTriggerInputScrollPosition = useCallback(() => {
    const input = triggerInputRef.current

    if (!input) {
      return
    }

    input.scrollLeft = 0
    input.setSelectionRange?.(0, 0)
  }, [])

  const scheduleTriggerInputScrollReset = useCallback(() => {
    requestAnimationFrame(() => {
      resetTriggerInputScrollPosition()
    })
  }, [resetTriggerInputScrollPosition])

  useEffect(() => {
    values && setHighlightValidation(true)
    if (!isEqualTo(selectedOptionsRef.current, selectedOptions)) {
      setCurrentOptions(selectedOptions)
      selectedOptionsRef.current = selectedOptions
    }
  }, [selectedOptions, values])

  useEffect(() => {
    scheduleTriggerInputScrollReset()
  }, [open, scheduleTriggerInputScrollReset, values])

  useEffect(() => {
    requestAnimationFrame(() => {
      const input = triggerInputRef.current

      if (!input || open) {
        return
      }

      if (document.activeElement === input) {
        input.blur()
      }

      resetTriggerInputScrollPosition()
    })
  }, [open, resetTriggerInputScrollPosition])

  const handleFocus = () => {
    scheduleTriggerInputScrollReset()
    onFocus?.()
  }

  const handleOpen = () => {
    setOpen((prev) => !prev)
    scheduleTriggerInputScrollReset()
  }

  const onLostFocus = () => {
    onBlur && onBlur()
    if (!isEqualTo(previousOptions, currentOptions)) {
      onBlurTracking && onBlurTracking(currentOptions, isUnchanged)
      isUnchanged && setIsUnchanged(false)
      setPreviousOptions(currentOptions)
    }
  }

  const isSelected = (item: DropdownOption) => currentOptions.map(optionToValue).includes(item.value)

  const onSelectChange = (newValue: DropdownOption[]) => {
    setHighlightValidation(true)
    onChange(newValue.map(optionToValue))
    setTouched(name)
  }

  function handleNoInformation(selectedOption: DropdownOption): DropdownOption[] {
    return [selectedOption]
  }

  function handleRegularSelect(selectedOption: DropdownOption): DropdownOption[] {
    if (!isSelected(selectedOption)) {
      return [...[...currentOptions].filter(notNull).filter(removeNoInformation), selectedOption]
    } else {
      return currentOptions.filter((o) => o.value !== selectedOption.value)
    }
  }

  const selectItem = (selectedOption: DropdownOption) => {
    let newValue

    if (isNoInformation(selectedOption)) {
      newValue = handleNoInformation(selectedOption)
    } else {
      newValue = handleRegularSelect(selectedOption)
    }

    setCurrentOptions(newValue)
    onSelectChange(newValue)
  }

  const dropdownValue = () => {
    return dropdownOptions
      .filter((o) => o.value && currentOptions.map((item) => item.value).includes(o.value))
      .map((o) => o.label)
      .join(', ')
  }

  const isVisiblePalm = `desk-hide lap-hide ${open ? '' : ' palm-hide'}`

  return (
    <>
      <div data-testid={`${id}-palm-backdrop`} className={`${isVisiblePalm} ${styles.palmBackdrop}`} />
      <div id={`${id}-select-container`} data-testid={`${id}-select-container`} className='select-container'>
        {label && <InputLabel id={id} labelTranslationKey={label} propertyName={name} labelClass={labelClassName} />}
        <div>
          <Dropdown.Root
            isOpen={open}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                handleClose()
              } else {
                setOpen(true)
              }
            }}
            className='select-input-wrapper one-whole'
          >
            <Dropdown.Trigger id={id} aria-controls={open ? `${id}-dropdown-list` : undefined}>
              <TextInput.Root
                id={id}
                data-testid={`${id}-select-text-input`}
                invalid={isInvalid}
                style={inputStyles}
                {...(disabled && { disabled: true })}
                {...(ariaLabel && { 'aria-label': ariaLabel })}
              >
                <TextInput.Input
                  ref={triggerInputRef}
                  className={`${styles.triggerInput} font-ellipsis`}
                  value={dropdownValue()}
                  onClick={handleOpen}
                  onFocus={handleFocus}
                  placeholder={placeholder ? placeholder : ''}
                  readOnly
                />
              </TextInput.Root>
            </Dropdown.Trigger>
            {open && (
              <Dropdown.List id={`${id}-dropdown-list`} as='div' className={styles.dropdownList} isCondensed={true}>
                {dropdownOptions
                  .filter((o) => o.value != null && o.label)
                  .map((option, index) => (
                    <Dropdown.Item
                      id={`${index}-${option.value}`}
                      key={index}
                      role='menuitemcheckbox'
                      aria-checked={isSelected(option)}
                      closeOnSelect={false}
                      onClick={() => !disabled && selectItem(option)}
                    >
                      <Checkbox.Root
                        id={`${id}-option-${index}`}
                        size='compact'
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Checkbox.Input checked={isSelected(option)} onChange={() => !disabled && selectItem(option)} />
                        <Checkbox.Label>{option.label!.toString()}</Checkbox.Label>
                      </Checkbox.Root>
                    </Dropdown.Item>
                  ))}
                <SaveSelectionButton label={t('wizard.screen.construction.save')} />
              </Dropdown.List>
            )}
          </Dropdown.Root>
        </div>
        {isInvalid && hasErrors() && (
          <div role='alert' className='font-error' aria-labelledby={id + '-input-label'}>
            <div className='caption caption--error'>
              <IconS24CrossCircle24Filled className={'caption-icon'} />
              {t(`validation.${errors[0]}`)}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default MultiSelectDropdown
